import { sanitizeHtml } from "utils/sanitizeHtml";
import { useData } from "data/data-provider";
import { isUsableUrl } from "utils/isUsableUrl";
import { sectionPaddingY } from "utils/sectionSpacing";

const WhyChooseUS = () => {
  const { whyUs } = useData();

  const hasHeader = Boolean(whyUs?.heading || whyUs?.description);
  const keypoints: any[] = whyUs?.keypoints ?? [];

  // 1NOW layout variant chosen in the FMS (Section.layout_variant):
  //   "card_grid" → 3-column card grid (current visual / default)
  //   "icon_list" → vertical list with icon-left, copy-right per row
  // Default to "card_grid" so tenants who haven't picked a layout get the
  // exact same render as before the picker existed.
  const layoutVariant: "card_grid" | "icon_list" =
    whyUs?.layout_variant === "icon_list" ? "icon_list" : "card_grid";

  return (
    <section
      className={`relative ${sectionPaddingY(
        whyUs?.vertical_spacing,
        "py-12 md:py-20 lg:py-28"
      )}`}
    >
      {/* Decorative gradient blob behind the section header — subtle, gives
          the area depth without competing with content. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 -top-10 mx-auto h-72 w-[min(720px,90%)] rounded-full opacity-40 blur-3xl"
        style={{
          background:
            "radial-gradient(circle at center, var(--c-primary-200, #2dd4bf) 0%, transparent 65%)",
        }}
      />

      {/* Section header */}
      {hasHeader && (
        <div className="relative max-w-3xl mx-auto text-center mb-10 sm:mb-14 md:mb-20">
          <span className="inline-block mb-4 px-4 py-1.5 text-[11px] sm:text-xs font-semibold tracking-[0.2em] uppercase rounded-full border border-primary-200/40 bg-primary-100/10 text-primary-300">
            Why Choose Us
          </span>
          {whyUs?.heading && (
            <h2
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] mb-4 sm:mb-6 text-text-color
                [&_p]:m-0 [&_p]:inline [&_strong]:font-extrabold [&_em]:italic [&_u]:underline"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(whyUs.heading) }}
            />
          )}
          {whyUs?.description && (
            <div
              className="text-base sm:text-lg lg:text-xl text-secondary-text-color leading-relaxed
                [&_p]:mb-2 last:[&_p]:mb-0
                [&_ul]:list-disc [&_ul]:ps-5 [&_ol]:list-decimal [&_ol]:ps-5 [&_li]:mb-1
                [&_strong]:font-semibold [&_em]:italic [&_u]:underline
                [&_a]:text-primary-200 [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(whyUs.description) }}
            />
          )}
        </div>
      )}

      {/* Feature grid: 1 col mobile, 2 col sm, 3 col lg, 4 col xl when there
          are 4+ items so they don't sit alone on the last row.
          Layout variant: `card_grid` (default) uses this grid;
          `icon_list` (handled below) uses a vertical list instead. */}
      {layoutVariant === "card_grid" && keypoints.length > 0 && (
        <div
          className={`relative grid gap-5 sm:gap-6 md:gap-8 grid-cols-1 sm:grid-cols-2 ${
            keypoints.length >= 4 ? "lg:grid-cols-2 xl:grid-cols-4" : "lg:grid-cols-3"
          }`}
        >
          {keypoints.map((feature: any, index: number) => {
            const number = String(index + 1).padStart(2, "0");
            return (
              <article
                key={index}
                className="group relative flex flex-col items-start overflow-hidden rounded-3xl border border-secondary-100 bg-secondary-50/70 p-6 sm:p-7 md:p-8
                  transition-[transform,border-color,box-shadow] duration-300 ease-out
                  hover:-translate-y-1.5 hover:border-primary-200 hover:shadow-2xl hover:shadow-primary-200/20"
              >
                {/* Top accent bar — fades in on hover */}
                <div
                  aria-hidden="true"
                  className="absolute inset-x-0 top-0 h-[3px] origin-left scale-x-0 bg-gradient-to-r from-primary-200 via-primary-300 to-primary-100 transition-transform duration-500 group-hover:scale-x-100"
                />

                {/* Number — sits in the corner like a chapter marker */}
                <span
                  aria-hidden="true"
                  className="absolute right-5 top-5 sm:right-6 sm:top-6 text-3xl sm:text-4xl md:text-5xl font-black leading-none text-primary-200/15 transition-colors duration-500 group-hover:text-primary-200/30"
                >
                  {number}
                </span>

                {/* Icon tile with gradient background. Falls back to nothing
                    if no image_url is set so cards stay clean either way. */}
                {isUsableUrl(feature?.image_url) && (
                  <div className="relative mb-5 sm:mb-6 flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-100/30 via-primary-200/20 to-primary-300/10 ring-1 ring-primary-200/30 transition-transform duration-500 group-hover:scale-110 group-hover:ring-primary-200/60">
                    <img
                      className="h-7 w-7 sm:h-9 sm:w-9 object-contain"
                      src={feature.image_url}
                      /* 1NOW-10440: alt="" alone already keeps these out of the
                         accessible name, but Design A pairs it with aria-hidden
                         and so does everything else decorative in this file (the
                         gradient bar, the corner number). One convention. */
                      alt=""
                      aria-hidden="true"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display =
                          "none";
                      }}
                    />
                  </div>
                )}

                <h3
                  className="relative z-10 text-lg sm:text-xl md:text-2xl font-bold mb-2.5 sm:mb-3 text-text-color
                    [&_p]:m-0 [&_strong]:font-extrabold [&_em]:italic [&_u]:underline"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(feature?.heading ?? "") }}
                />
                <div
                  className="relative z-10 text-sm sm:text-base text-secondary-text-color leading-relaxed
                    [&_p]:mb-2 last:[&_p]:mb-0
                    [&_ul]:list-disc [&_ul]:ps-5 [&_ol]:list-decimal [&_ol]:ps-5 [&_li]:mb-1
                    [&_strong]:font-semibold [&_em]:italic [&_u]:underline
                    [&_a]:text-primary-200 [&_a]:underline"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(feature?.description ?? "",) }}
                />
              </article>
            );
          })}
        </div>
      )}

      {/* Icon list layout — single column, each row is an icon on the
          left + heading & description on the right. Better for sections
          with many short reasons where the card grid feels noisy. Fills
          the full container width; the heading + description text inside
          each row carries its own readability cap below. */}
      {layoutVariant === "icon_list" && keypoints.length > 0 && (
        <div className="relative w-full flex flex-col divide-y divide-secondary-100">
          {keypoints.map((feature: any, index: number) => (
            <div
              key={index}
              className="flex items-start gap-5 sm:gap-6 py-5 sm:py-6 first:pt-0 last:pb-0"
            >
              {/* Icon tile — same gradient treatment as the cards so the
                  variant feels consistent. Falls back gracefully when no
                  image_url is set: shows a neutral primary-tinted square. */}
              <div className="flex h-12 w-12 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-100/30 via-primary-200/20 to-primary-300/10 ring-1 ring-primary-200/30">
                {isUsableUrl(feature?.image_url) ? (
                  <img
                    className="h-6 w-6 sm:h-7 sm:w-7 object-contain"
                    src={feature.image_url}
                    /* Decorative — see the note on the card-grid icon above. */
                    alt=""
                    aria-hidden="true"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display =
                        "none";
                    }}
                  />
                ) : (
                  <span className="h-2 w-2 rounded-full bg-primary-200" />
                )}
              </div>
              {/* Content side — max-w-4xl on text caps reading line length
                  at ~896px (a comfortable ~80ch) while the row itself can
                  span the full container width below. */}
              <div className="min-w-0 flex-1 max-w-4xl">
                <h3
                  className="text-base sm:text-lg md:text-xl font-bold mb-1.5 text-text-color
                    [&_p]:m-0 [&_strong]:font-extrabold [&_em]:italic [&_u]:underline"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(feature?.heading ?? "") }}
                />
                <div
                  className="text-sm sm:text-base text-secondary-text-color leading-relaxed
                    [&_p]:mb-2 last:[&_p]:mb-0
                    [&_ul]:list-disc [&_ul]:ps-5 [&_ol]:list-decimal [&_ol]:ps-5 [&_li]:mb-1
                    [&_strong]:font-semibold [&_em]:italic [&_u]:underline
                    [&_a]:text-primary-200 [&_a]:underline"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(feature?.description ?? "",) }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default WhyChooseUS;
